import { AdminAccountingScreen, AdminGermanVATScreen } from './admin/accounting';
import { AdminAssetsScreen, AdminIconsScreen } from './admin/assets';
import { AdminBookingsScreen, AdminPurchasesScreen } from './admin/bookings';
import { AdminCitiesScreen } from './admin/cities';
import { AdminTablesScreen } from './admin/dd_tables';
import { AdminTypesScreen } from './admin/dd_types';
import { AdminBankTransfersScreen, AdminEconomyPendingScreen, AdminPayoutsScreen } from './admin/economy';
import { AdminEventScreen, AdminRepeatSessionScreen, AdminSessionScreen } from './admin/event';
import { AdminEventBookingsScreen } from './admin/event_bookings';
import { AdminTagsScreen } from './admin/event_tags';
import { AdminEventsScreen, AdminOccupancyScreen, AdminReviewsScreen, AdminUpcomingScreen } from './admin/events';
import { AdminGiftcardsScreen } from './admin/giftcards';
import { AdminImportedEventsScreen, AdminLinkedOrdersScreen, AdminNewOrdersScreen, AdminProvidersScreen } from './admin/integrations';
import { AdminLoginScreen } from './admin/login';
import { AdminLastMessagesScreen, AdminMessageStatisticsScreen } from './admin/messages';
import { AdminNewAnalyticsScreen, AdminNewFlowsScreen, AdminNewFunnelsScreen, AdminNewRetentionScreen, AdminNewSQLScreen } from './admin/new_statistics';
import { AdminPageScreen, AdminPagesScreen, AdminTranslateScreen } from './admin/pages';
import { AdminIntentsScreen } from './admin/payment_intents';
import { AdminPdfFailuresScreen, AdminPdfSpecsScreen, AdminPdfToolScreen } from './admin/pdf';
import { AdminProducerScreen, AdminProducersScreen, AdminProducerUsersScreen } from './admin/producers';
import { AdminCodesScreen, AdminPromosScreen } from './admin/promos';
import { AdminRouteScreen, AdminRoutesScreen } from './admin/routes';
import { AdminAnalyticsScreen, AdminBookingsStatisticsScreen, AdminMembersScreen, AdminRetentionScreen, AdminSegmentScreen } from './admin/statistics';
import { AdminAccessKeysScreen, AdminAPIToolScreen, AdminJobsScreen, AdminLogScreen, AdminLogsScreen, AdminNetworkAccessScreen, AdminSecretsScreen } from './admin/system';
import { AdminSendMessageScreen, AdminTemplateScreen, AdminTemplatesScreen } from './admin/templates';
import { AdminTranslationsScreen } from './admin/translations';
import { AdminUserScreen } from './admin/user';
import { AdminCommentsScreen, AdminSuspiciousScreen, AdminUsersScreen } from './admin/users';
import { AdminVenuesScreen } from './admin/venues';
import { AdminVideosScreen } from './admin/video';
import { aInitTrack } from './lib/mdl/a-track';
import { canManageBookings, canManageEconomy, canManageEmail, canManageEvents, canManagePages, canManagePDF, canManageProducers, canManageRoutes, canManageSystem, canManageUsers, canManageVenues, canManageVideo, canViewGiftCards, canViewStatistics, canViewTranslations } from './lib/me/can';
import { meAutoLogin, meLogout, the_login_scope } from './lib/me/login';
import { UIBaseInput } from './lib/ui/component/input-base';
import { uiBuildNavigationMenu } from './lib/ui/component/navigation-menu';
import { $composer } from './lib/ui/composer';
import { $el } from './lib/ui/elements';
import { xavis_experiments } from './lib/y/debug';
import { $location, YRoute } from './lib/y/location';
import { y_ready } from './lib/y/util';

UIBaseInput.default_kind = 'k-subtle';
$location.title_suffix = 'Abundo Admin';

const [menu, menu_routes] = uiBuildNavigationMenu([
    { widget: () => $el.DIV('ui-logo') },
    { space: 1 },
    {
        path: 'users', title: 'Users', secondary: [
            { path: 'list', title: 'List', screen: () => new AdminUsersScreen, when: () => canManageUsers() },
            { path: 'giftcards', title: 'Gift cards', screen: () => new AdminGiftcardsScreen, when: () => canViewGiftCards() },
            { path: 'comments', title: 'Comments', screen: () => new AdminCommentsScreen, when: () => canManageUsers() },
            { path: 'promos', title: 'Promos', screen: () => new AdminPromosScreen, when: () => canManageUsers() },
            { path: 'codes', title: 'Codes', screen: () => new AdminCodesScreen, when: () => canManageUsers() },
            { path: 'suspicious', title: 'Suspicious', screen: () => new AdminSuspiciousScreen, when: () => canManageUsers() },
            { path: 'payments', title: 'Payments', screen: () => new AdminIntentsScreen, when: () => canManageUsers() && xavis_experiments }
        ], when: () => canManageUsers() || canViewGiftCards()
    },
    {
        path: 'venues', title: 'Venues', secondary: [
            { path: 'list', title: 'List', screen: () => new AdminVenuesScreen },
            { path: 'cities', title: 'Cities', screen: () => new AdminCitiesScreen }
        ], when: () => canManageVenues()
    },
    {
        path: 'producers', title: 'Producers', secondary: [
            { path: 'list', title: 'List', screen: () => new AdminProducersScreen },
            { path: 'users', title: 'Users', screen: () => new AdminProducerUsersScreen }
        ], when: () => canManageProducers()
    },
    {
        path: 'events', title: 'Events', secondary: [
            { path: 'list', title: 'List', screen: () => new AdminEventsScreen },
            { path: 'tags', title: 'Tags', screen: () => new AdminTagsScreen },
            { path: 'reviews', title: 'Reviews', screen: () => new AdminReviewsScreen },
            { path: 'upcoming', title: 'Upcoming sessions', screen: () => new AdminUpcomingScreen },
            { path: 'occupancy', title: 'Occupancy', screen: () => new AdminOccupancyScreen },
        ], when: () => canManageEvents()
    },
    {
        path: 'integrations', title: 'Integrations', secondary: [
            { path: 'events', title: 'Events', screen: () => new AdminImportedEventsScreen },
            { path: 'providers', title: 'Providers', screen: () => new AdminProvidersScreen },
            { path: 'new_orders', title: 'New orders', screen: () => new AdminNewOrdersScreen },
            { path: 'linked_orders', title: 'Linked orders', screen: () => new AdminLinkedOrdersScreen }
        ], when: () => canManageEvents()
    },
    {
        path: 'bookings', title: 'Bookings', secondary: [
            { path: 'list', title: 'List', screen: () => new AdminBookingsScreen },
            { path: 'purchases', title: 'Purchases', screen: () => new AdminPurchasesScreen },
        ], when: () => canManageBookings()
    },
    {
        path: 'economy', title: 'Economy', secondary: [
            { path: 'pending', title: 'Pending', screen: () => new AdminEconomyPendingScreen },
            { path: 'payouts', title: 'Payouts', screen: () => new AdminPayoutsScreen },
            { path: 'bank_transfers', title: 'Bank transfers', screen: () => new AdminBankTransfersScreen },
            { path: 'accounting', title: 'Accounting', screen: () => new AdminAccountingScreen, when: xavis_experiments },
            { path: 'german_vat', title: 'German VAT', screen: () => new AdminGermanVATScreen },
        ], when: () => canManageEconomy()
    },
    {
        path: 'pages', title: 'Pages', secondary: [
            { path: 'routes', title: 'Routes', screen: () => new AdminRoutesScreen, when: () => canManageRoutes() },
            { path: 'pages', title: 'Pages', screen: () => new AdminPagesScreen, when: () => canManagePages() },
            { path: 'translations', title: 'Translations', screen: () => new AdminTranslationsScreen, when: () => canViewTranslations() },
            { path: 'assets', title: 'Assets', screen: () => new AdminAssetsScreen, when: () => xavis_experiments },
            { path: 'icons', title: 'Icons', screen: () => new AdminIconsScreen, when: () => xavis_experiments },
        ], when: () => canManagePages() || canManageRoutes() || canViewTranslations()
    },
    {
        path: 'pdf', title: 'PDF', secondary: [
            { path: 'failures', title: 'Failed', screen: () => new AdminPdfFailuresScreen },
            { path: 'specs', title: 'Specs', screen: () => new AdminPdfSpecsScreen },
            { path: 'tool', title: 'Tool', screen: () => new AdminPdfToolScreen },
        ], when: () => canManagePDF()
    },
    {
        path: 'statistics', title: 'Statistics', secondary: [
            { path: 'members', title: 'Members', screen: () => new AdminMembersScreen },
            { path: 'bookings', title: 'Bookings', screen: () => new AdminBookingsStatisticsScreen },
            { path: 'analytics', title: 'Analytics', screen: () => new AdminAnalyticsScreen },
            { path: 'retention', title: 'Retention', screen: () => new AdminRetentionScreen },
        ], when: () => canViewStatistics()
    },
    {
        path: 'new_statistics', title: 'Stats+', secondary: [
            { path: 'retention', title: 'Retention', screen: () => new AdminNewRetentionScreen, when: xavis_experiments },
            { path: 'analytics', title: 'Analytics', screen: () => new AdminNewAnalyticsScreen, when: xavis_experiments },
            { path: 'flows', title: 'Flows', screen: () => new AdminNewFlowsScreen, when: xavis_experiments },
            { path: 'funnels', title: 'Funnels', screen: () => new AdminNewFunnelsScreen, when: xavis_experiments },
            { path: 'segment', title: 'Segment', screen: () => new AdminSegmentScreen, when: xavis_experiments },
            { path: 'sql', title: 'SQL', screen: () => new AdminNewSQLScreen, when: xavis_experiments },
        ], when: xavis_experiments
    },
    {
        path: 'system', title: 'System', secondary: [
            { path: 'logs', title: 'Logs', screen: () => new AdminLogsScreen },
            { path: 'jobs', title: 'Jobs', screen: () => new AdminJobsScreen },
            { path: 'access_keys', title: 'Access keys', screen: () => new AdminAccessKeysScreen },
            { path: 'api_tool', title: 'API tool', screen: () => new AdminAPIToolScreen },
            { path: 'data_types', title: 'Types', screen: () => new AdminTypesScreen },
            { path: 'data_tables', title: 'Tables', screen: () => new AdminTablesScreen },
            { path: 'network_access', title: 'Network access', screen: () => new AdminNetworkAccessScreen },
            { path: 'secrets', title: 'Secrets', screen: () => new AdminSecretsScreen },
        ], when: () => canManageSystem()
    },
    {
        path: 'video', title: 'Video', secondary: [
            { path: 'list', title: 'List', screen: () => new AdminVideosScreen },
        ], when: () => canManageVideo()
    },
    {
        path: 'messages', title: 'Messages', secondary: [
            { path: 'last', title: 'Last sent', screen: () => new AdminLastMessagesScreen },
            { path: 'statistics', title: 'Statistics', screen: () => new AdminMessageStatisticsScreen },
            { path: 'templates', title: 'Templates', screen: () => new AdminTemplatesScreen }
        ], when: () => canManageEmail()
    },
    {
        title: 'Log out', action: () => {
            meLogout();
            $location.navigate([]);
        }
    }
]);

const routes: YRoute[] = [{
    match: () => !the_login_scope,
    path: [],
    screen: () => new AdminLoginScreen
}, {
    match: () => !the_login_scope,
    redirect: () => [],
},
...menu_routes,
{
    match: () => xavis_experiments,
    path: ['log', '%log_id'],
    screen: m => new AdminLogScreen(m.log_id!)
}, {
    match: () => !!the_login_scope,
    path: [],
    redirect: () => menu.first_path
}, {
    redirect: () => []
}, {
    name: 'user',
    screen: m => new AdminUserScreen(m.user_id),
}, {
    name: 'producer',
    screen: m => new AdminProducerScreen(m.producer_id),
}, {
    name: 'event',
    screen: m => new AdminEventScreen(m.event_id, m.duplicate, m.imported_id)
}, {
    name: 'event_bookings',
    screen: m => new AdminEventBookingsScreen(m.session_id!)
}, {
    name: 'session',
    screen: m => new AdminSessionScreen(m.event_id!, m.session_id)
}, {
    name: 'repeat_session',
    screen: m => new AdminRepeatSessionScreen(m.event_id!, m.session_id!)
}, {
    name: 'page',
    screen: m => new AdminPageScreen(m.page_id)
}, {
    name: 'translate_page',
    screen: m => new AdminTranslateScreen(m.page_id!)
}, {
    name: 'template',
    screen: m => new AdminTemplateScreen(m.template_id!)
}, {
    name: 'send_message',
    screen: m => new AdminSendMessageScreen(m.channel!, m.session_id!, m.user_id!, m.languages!)
}, {
    name: 'route',
    screen: m => new AdminRouteScreen(m.route_id!, m.options)
}];

aInitTrack(false);

const login_response = meAutoLogin('admin', {});

y_ready.then(() => {
    login_response.then(() => {
        $location.init(routes, '/', $composer);
    });
});
